import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Chip,
  Avatar,
  Paper,
  Divider,
  Checkbox,
  FormControl,
  FormGroup,
  RadioGroup,
  FormControlLabel,
  Radio,
  Link,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  setDoc,
  Timestamp,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import {
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Person,
  Email,
  Phone,
  ArrowBackIosNew,
  LocationOn,
  CalendarToday,
  Language,
  NotificationsActive,
  AccountBalance,
  CreditCard,
  VerifiedUser,
  Article,
  School,
  Videocam,
  Verified,
  Warning,
  Comment,
} from "@mui/icons-material";
import ProviderDocumentReviewList from "../components/documentUploads/ProviderDocumentReviewList";
import { getProviderDocumentsForReview } from "../utils/documentUploadAdminService";

const REJECTION_REASON_OPTIONS = [
  {
    value: "id_proof_invalid",
    label: "ID proof is missing, unclear, or invalid",
  },
  {
    value: "visa_or_nationality_issue",
    label: "Visa or nationality details are incomplete/invalid",
  },
  {
    value: "banking_details_issue",
    label: "Banking details are incomplete or incorrect",
  },
  {
    value: "business_details_issue",
    label: "Business name or ABN details are missing/invalid",
  },
  {
    value: "resume_missing",
    label: "Resume/CV is missing or not acceptable",
  },
  {
    value: "certificate_missing",
    label: "Certificates are missing, expired, or unclear",
  },
  {
    value: "verification_video_issue",
    label: "Verification video is missing or not clear",
  },
  {
    value: "profile_info_incomplete",
    label: "Profile information is incomplete",
  },
];

const REUPLOAD_OPTIONS = [
  { value: "passport", label: "Passport" },
  { value: "drivingLicence", label: "Driving Licence" },
  { value: "resume", label: "Resume/CV" },
  { value: "certificates", label: "Certificates" },
  { value: "verificationVideo", label: "Verification Video" },
  { value: "idProof", label: "ID Proof (Legacy)" },
  { value: "visaDetails", label: "Visa/Nationality Details" },
  { value: "bankingDetails", label: "Banking Details" },
  { value: "businessDetails", label: "Business Details" },
];

const createEmptyRejectionForm = () => ({
  reasons: [],
  customReason: "",
  requestReupload: false,
  reuploadItems: [],
  missingDetails: "",
});

const REQUIRED_DOCUMENT_LABELS = {
  identity: "Passport or Driving Licence",
  resume: "Resume/CV",
  certificates: "Certificates",
};

const APPROVED_DOCUMENT_STATUSES = new Set(["approved"]);
const ACTIVE_DOCUMENT_STATUSES = new Set([
  "uploaded",
  "submitted",
  "approved",
  "pending_review",
  "rejected",
  "reupload_required",
]);

const DOCUMENT_TYPE_ALIASES = {
  passport: "passport",
  idproof: "passport",
  id_proof: "passport",
  drivinglicence: "driving_licence",
  driving_licence: "driving_licence",
  drivinglicense: "driving_licence",
  driving_license: "driving_licence",
  resume: "resume_cv",
  resume_cv: "resume_cv",
  cv: "resume_cv",
  certificate: "certificates",
  certificates: "certificates",
  verificationvideo: "verification_video",
  verification_video: "verification_video",
};

const LEGACY_DOCUMENT_KEYS = {
  passport: ["passportUrl", "passport", "idProofUrl", "idProof"],
  driving_licence: [
    "drivingLicenceUrl",
    "drivingLicenseUrl",
    "drivingLicence",
    "drivingLicense",
  ],
  resume_cv: ["resumeUrl", "resume", "cvUrl", "cv"],
  certificates: ["certificatesUrl", "certificates", "certificateUrl", "certificate"],
};

const normalizeReviewDocumentType = (value) => {
  const normalized = String(value || "")
    .trim()
    .replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
    .replace(/[\s-]+/g, "_")
    .toLowerCase()
    .replace(/__+/g, "_");
  const compact = normalized.replace(/_/g, "");
  return DOCUMENT_TYPE_ALIASES[normalized] || DOCUMENT_TYPE_ALIASES[compact] || normalized;
};

const reviewDocumentMillis = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getReviewDocumentTime = (document) =>
  reviewDocumentMillis(document?.updatedAt) ||
  reviewDocumentMillis(document?.uploadedAt) ||
  reviewDocumentMillis(document?.reviewedAt) ||
  0;

const getReviewDocumentSlotKey = (document) =>
  `${normalizeReviewDocumentType(document?.documentType)}:${document?.side || "single"}`;

const getLatestReviewDocuments = (documents = []) => {
  const bySlot = new Map();

  documents.forEach((document) => {
    if (!document?.id && !document?.documentType) return;

    const status = String(document.status || document.reviewStatus || "").toLowerCase();
    if (status && !ACTIVE_DOCUMENT_STATUSES.has(status)) return;

    const slotKey = getReviewDocumentSlotKey(document);
    const existing = bySlot.get(slotKey);
    if (!existing || getReviewDocumentTime(document) >= getReviewDocumentTime(existing)) {
      bySlot.set(slotKey, document);
    }
  });

  return Array.from(bySlot.values());
};

const isReviewDocumentApproved = (document) =>
  APPROVED_DOCUMENT_STATUSES.has(String(document?.reviewStatus || "").toLowerCase()) ||
  APPROVED_DOCUMENT_STATUSES.has(String(document?.status || "").toLowerCase());

const extractReviewDocumentUrl = (value) => {
  if (!value) return "";

  if (typeof value === "string") return value.trim();

  if (Array.isArray(value)) {
    for (const item of value) {
      const url = extractReviewDocumentUrl(item);
      if (url) return url;
    }
    return "";
  }

  if (typeof value === "object") {
    const directUrl =
      value.signedUrl ||
      value.url ||
      value.downloadURL ||
      value.downloadUrl ||
      value.fileUrl ||
      value.uri;

    if (typeof directUrl === "string" && directUrl.trim()) {
      return directUrl.trim();
    }

    return extractReviewDocumentUrl(value.files || value.items || value.documents || []);
  }

  return "";
};

const hasLegacyDocumentForType = (legacyDocuments, documentType) => {
  const keys = LEGACY_DOCUMENT_KEYS[documentType] || [];
  return keys.some((key) => extractReviewDocumentUrl(legacyDocuments?.[key]));
};

const resolveApprovalReadiness = ({
  secureDocuments = [],
  providerData = {},
  onboardingData = {},
}) => {
  const latestDocuments = getLatestReviewDocuments(secureDocuments);
  const legacyDocuments = {
    ...(providerData?.documents || {}),
    ...(providerData?.provider_onboarding?.documents || {}),
    ...(onboardingData?.documents || {}),
  };

  const docsByType = latestDocuments.reduce((acc, document) => {
    const type = normalizeReviewDocumentType(document.documentType);
    if (!acc[type]) acc[type] = [];
    acc[type].push(document);
    return acc;
  }, {});

  const hasApprovedSecureType = (type) =>
    (docsByType[type] || []).some(isReviewDocumentApproved);

  const hasCurrentSecureType = (type) => (docsByType[type] || []).length > 0;
  const hasDocumentForType = (type) =>
    hasApprovedSecureType(type) ||
    (!hasCurrentSecureType(type) && hasLegacyDocumentForType(legacyDocuments, type));

  const missing = [];
  const hasIdentity =
    hasDocumentForType("passport") || hasDocumentForType("driving_licence");

  if (!hasIdentity) {
    missing.push(REQUIRED_DOCUMENT_LABELS.identity);
  }

  ["resume_cv", "certificates"].forEach((type) => {
    if (!hasDocumentForType(type)) {
      missing.push(type === "resume_cv" ? REQUIRED_DOCUMENT_LABELS.resume : REQUIRED_DOCUMENT_LABELS.certificates);
    }
  });

  return {
    ready: missing.length === 0,
    missing,
    latestDocuments,
  };
};

const ClientApprovals = () => {
  const [pendingClients, setPendingClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    client: null,
    action: null,
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewDialogData, setViewDialogData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [currentClientId, setCurrentClientId] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState({});
  const [rejectionDialog, setRejectionDialog] = useState({
    open: false,
    client: null,
  });
  const [rejectionForm, setRejectionForm] = useState(
    createEmptyRejectionForm(),
  );
  const [rejectionSubmitting, setRejectionSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState({
    open: false,
    url: "",
    title: "",
  });

  // Fetch pending clients from Firestore
  const fetchPendingClients = async () => {
    try {
      setLoading(true);
      setError("");

      console.log("Fetching pending clients...");

      // First get all clients, then filter for pending ones
      const q = query(
        collection(db, "users"),
        where("roles.client", "==", true),
      );

      const querySnapshot = await getDocs(q);
      console.log("Total clients found:", querySnapshot.size);

      const clientsData = [];

      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        console.log("Client data:", {
          id: doc.id,
          approvalStatus: userData.approvalStatus,
          name: userData.name,
        });

        // Only include clients with pending status (default to approved if no status)
        const approvalStatus = userData.approvalStatus || "approved";
        if (approvalStatus === "pending") {
          clientsData.push({
            id: doc.id,
            ...userData,
          });
        }
      });

      console.log("Pending clients found:", clientsData.length);

      // Sort by creation date (newest first)
      clientsData.sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() || new Date(0);
        const bDate = b.createdAt?.toDate?.() || new Date(0);
        return bDate - aDate;
      });

      setPendingClients(clientsData);
    } catch (err) {
      console.error("Error fetching pending clients:", err);
      console.error("Error details:", err.message);
      console.error("Error code:", err.code);

      // More specific error messages
      if (err.code === "permission-denied") {
        setError(
          "Permission denied. Please check your Firestore security rules allow admin access to user documents.",
        );
      } else if (err.code === "failed-precondition") {
        setError(
          "Database query failed. This might be due to missing indexes.",
        );
      } else {
        setError(`Failed to fetch pending client applications: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingClients();
  }, []);

  // Handle approval confirmation
  const handleApprovalAction = (client, action) => {
    if (!client) return;

    if (action === "rejected") {
      setRejectionDialog({ open: true, client });
      setRejectionForm(createEmptyRejectionForm());
      return;
    }

    setConfirmDialog({ open: true, client, action });
  };

  const parseExpiryToTimestamp = (value) => {
    if (!value) return null;

    const normalizeDate = (dateValue) => {
      if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
        return null;
      }
      return new Date(
        dateValue.getFullYear(),
        dateValue.getMonth(),
        dateValue.getDate(),
      );
    };

    try {
      if (value instanceof Date) {
        const normalized = normalizeDate(value);
        return normalized ? Timestamp.fromDate(normalized) : null;
      }

      if (typeof value === "string") {
        const parsed = new Date(value);
        const normalized = normalizeDate(parsed);
        return normalized ? Timestamp.fromDate(normalized) : null;
      }

      if (typeof value === "object") {
        if (typeof value.toDate === "function") {
          const normalized = normalizeDate(value.toDate());
          return normalized ? Timestamp.fromDate(normalized) : null;
        }

        if (typeof value.seconds === "number") {
          const normalized = normalizeDate(new Date(value.seconds * 1000));
          return normalized ? Timestamp.fromDate(normalized) : null;
        }

        if (value.year && value.month && value.day) {
          const day = parseInt(value.day, 10);
          const month = parseInt(value.month, 10);
          const year = parseInt(value.year, 10);
          if (!Number.isNaN(day) && !Number.isNaN(month) && !Number.isNaN(year)) {
            const normalized = normalizeDate(new Date(year, month - 1, day));
            return normalized ? Timestamp.fromDate(normalized) : null;
          }
        }
      }
    } catch (err) {
      console.warn("Failed to parse expiry timestamp:", err);
    }

    return null;
  };

  // Confirm approval/rejection
  const confirmApprovalAction = async () => {
    const { client, action } = confirmDialog;
    if (!client) return;

    if (action === "rejected") {
      setConfirmDialog({ open: false, client: null, action: null });
      setRejectionDialog({ open: true, client });
      setRejectionForm(createEmptyRejectionForm());
      return;
    }

    try {
      const now = Timestamp.now();
      const providerOnboardingRef = doc(
        db,
        "users",
        client.id,
        "details",
        "provider_onboarding",
      );
      let onboardingDataForApproval = {};

      if (action === "approved") {
        const onboardingSnap = await getDoc(providerOnboardingRef).catch(() => null);
        onboardingDataForApproval = onboardingSnap?.exists?.()
          ? onboardingSnap.data() || {}
          : {};
      }

      if (action === "approved") {
        const secureDocsResult = await getProviderDocumentsForReview({
          providerId: client.id,
        }).catch(() => ({ documents: [] }));

        const readiness = resolveApprovalReadiness({
          secureDocuments: secureDocsResult.documents || [],
          providerData: client,
          onboardingData: onboardingDataForApproval,
        });

        if (!readiness.ready) {
          showSnackbar(
            `Approve required provider documents before approving the application: ${readiness.missing.join(", ")}.`,
            "warning",
          );
          return;
        }
      }

      const updateData = {
        approvalStatus: action,
        approvedAt: action === "approved" ? now : null,
        rejectedAt: null,
        rejectionReason: null,
        rejectionReasons: [],
        rejectionMeta: null,
        updatedAt: now,
      };

      if (action === "approved") {
        updateData.providerAccountDisabled = false;
        updateData.accountDisabled = false;
        updateData.disabledReason = null;

        try {
          const onboardingData = onboardingDataForApproval || {};

          const passportExpiry =
            (onboardingData.passport && onboardingData.passport.expiry) ||
            onboardingData.passportExpiry ||
            onboardingData.passportExpiryTimestamp ||
            (onboardingData.profile && onboardingData.profile.passportExpiry) ||
            null;
          const passportExpiryTimestamp = parseExpiryToTimestamp(passportExpiry);
          if (passportExpiryTimestamp) {
            updateData.passportExpiryTimestamp = passportExpiryTimestamp;
            updateData.passportStatus = "active";
            updateData.passportReminderLastSentAt = null;
            updateData.passportRemindersSentCount = 0;
            updateData.passportFinalNoticeSentAt = null;
            updateData.passportTerminationNoticeSentAt = null;
          }

          const drivingExpiry =
            (onboardingData.drivingLicence && onboardingData.drivingLicence.expiry) ||
            (onboardingData.drivingLicense && onboardingData.drivingLicense.expiry) ||
            onboardingData.drivingLicenceExpiry ||
            onboardingData.drivingLicenseExpiry ||
            onboardingData.drivingLicenceExpiryTimestamp ||
            onboardingData.drivingLicenseExpiryTimestamp ||
            (onboardingData.profile && onboardingData.profile.drivingLicenceExpiry) ||
            (onboardingData.profile && onboardingData.profile.drivingLicenseExpiry) ||
            null;
          const drivingExpiryTimestamp = parseExpiryToTimestamp(drivingExpiry);
          if (drivingExpiryTimestamp) {
            updateData.drivingLicenceExpiryTimestamp = drivingExpiryTimestamp;
            updateData.drivingLicenseExpiryTimestamp = drivingExpiryTimestamp;
            updateData.drivingLicenceStatus = "active";
            updateData.drivingLicenseStatus = "active";
            updateData.drivingLicenceReminderLastSentAt = null;
            updateData.drivingLicenceRemindersSentCount = 0;
            updateData.drivingLicenceFinalNoticeSentAt = null;
            updateData.drivingLicenceTerminationNoticeSentAt = null;
          }
        } catch (expiryErr) {
          console.warn("Failed to sync approval expiry fields:", expiryErr);
        }
      }

      await updateDoc(doc(db, "users", client.id), updateData);

      // Also update provider_onboarding inside details sub-collection (merge if missing)
      try {
        await setDoc(
          providerOnboardingRef,
          {
            status: action,
            approvedAt: action === "approved" ? now : null,
            rejectedAt: null,
            rejectionReason: null,
            rejectionReasons: [],
            rejectionMeta: null,
            updatedAt: now,
          },
          { merge: true },
        );
      } catch (subErr) {
        console.warn("Failed to update provider_onboarding doc:", subErr);
      }

      // Remove from pending list
      setPendingClients((prev) => prev.filter((c) => c.id !== client.id));

      const actionText = action === "approved" ? "approved" : "rejected";
      showSnackbar(
        `Client ${client.name} has been ${actionText} successfully`,
        "success",
      );
    } catch (err) {
      console.error(`Error ${action} client:`, err);
      showSnackbar(`Failed to ${action} client`, "error");
    } finally {
      setConfirmDialog({ open: false, client: null, action: null });
    }
  };

  // Format date helper
  const formatDate = (dateField) => {
    if (!dateField) return "N/A";
    if (dateField.toDate && typeof dateField.toDate === "function") {
      return dateField.toDate().toLocaleDateString();
    }
    if (dateField instanceof Date) {
      return dateField.toLocaleDateString();
    }
    if (typeof dateField === "string") {
      try {
        return new Date(dateField).toLocaleDateString();
      } catch {
        return dateField;
      }
    }
    // Support RN-style date map objects: { day, month, year }
    if (
      typeof dateField === "object" &&
      dateField !== null &&
      (dateField.year !== undefined || dateField.Y !== undefined) &&
      dateField.month !== undefined &&
      dateField.day !== undefined
    ) {
      const day = String(dateField.day).padStart(2, "0");
      const month = String(dateField.month).padStart(2, "0");
      const year = String(dateField.year || dateField.Y);
      return `${day}/${month}/${year}`;
    }
    return "N/A";
  };

  const getFirstNonEmptyValue = (...values) => {
    for (const value of values) {
      if (value === undefined || value === null) continue;
      if (typeof value === "string" && value.trim().length === 0) continue;
      return value;
    }
    return null;
  };

  const resolveDocumentExpiryForRequest = (
    topLevelData,
    onboardingData,
    documentType,
  ) => {
    if (documentType === "passport") {
      return getFirstNonEmptyValue(
        topLevelData?.passportExpiryTimestamp,
        topLevelData?.passportExpiry,
        topLevelData?.passport?.expiry,
        topLevelData?.profile?.passportExpiry,
        topLevelData?.provider_onboarding?.passportExpiryTimestamp,
        topLevelData?.provider_onboarding?.passportExpiry,
        topLevelData?.provider_onboarding?.passport?.expiry,
        topLevelData?.provider_onboarding?.profile?.passportExpiry,
        onboardingData?.passportExpiryTimestamp,
        onboardingData?.passportExpiry,
        onboardingData?.passport?.expiry,
        onboardingData?.profile?.passportExpiry,
        onboardingData?.profile?.passport?.expiry,
      );
    }

    if (documentType === "drivingLicence") {
      return getFirstNonEmptyValue(
        topLevelData?.drivingLicenceExpiryTimestamp,
        topLevelData?.drivingLicenseExpiryTimestamp,
        topLevelData?.drivingLicenceExpiry,
        topLevelData?.drivingLicenseExpiry,
        topLevelData?.drivingLicence?.expiry,
        topLevelData?.drivingLicense?.expiry,
        topLevelData?.profile?.drivingLicenceExpiry,
        topLevelData?.profile?.drivingLicenseExpiry,
        topLevelData?.provider_onboarding?.drivingLicenceExpiryTimestamp,
        topLevelData?.provider_onboarding?.drivingLicenseExpiryTimestamp,
        topLevelData?.provider_onboarding?.drivingLicenceExpiry,
        topLevelData?.provider_onboarding?.drivingLicenseExpiry,
        topLevelData?.provider_onboarding?.drivingLicence?.expiry,
        topLevelData?.provider_onboarding?.drivingLicense?.expiry,
        topLevelData?.provider_onboarding?.profile?.drivingLicenceExpiry,
        topLevelData?.provider_onboarding?.profile?.drivingLicenseExpiry,
        onboardingData?.drivingLicenceExpiryTimestamp,
        onboardingData?.drivingLicenseExpiryTimestamp,
        onboardingData?.drivingLicenceExpiry,
        onboardingData?.drivingLicenseExpiry,
        onboardingData?.drivingLicence?.expiry,
        onboardingData?.drivingLicense?.expiry,
        onboardingData?.profile?.drivingLicenceExpiry,
        onboardingData?.profile?.drivingLicenseExpiry,
        onboardingData?.profile?.drivingLicence?.expiry,
        onboardingData?.profile?.drivingLicense?.expiry,
      );
    }

    return null;
  };

  // Show snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  // Open image preview
  const handleImageClick = (url, title) => {
    setImagePreview({ open: true, url, title });
  };

  // Close image preview
  const handleCloseImagePreview = () => {
    setImagePreview({ open: false, url: "", title: "" });
  };

  const closeRejectionDialog = () => {
    if (rejectionSubmitting) return;
    setRejectionDialog({ open: false, client: null });
    setRejectionForm(createEmptyRejectionForm());
  };

  const toggleRejectionReason = (value) => {
    setRejectionForm((prev) => ({
      ...prev,
      reasons: prev.reasons.includes(value)
        ? prev.reasons.filter((item) => item !== value)
        : [...prev.reasons, value],
    }));
  };

  const toggleReuploadItem = (value) => {
    setRejectionForm((prev) => ({
      ...prev,
      reuploadItems: prev.reuploadItems.includes(value)
        ? prev.reuploadItems.filter((item) => item !== value)
        : [...prev.reuploadItems, value],
    }));
  };

  const getRejectionReasonLabel = (value) =>
    REJECTION_REASON_OPTIONS.find((option) => option.value === value)?.label ||
    value;

  const getReuploadLabel = (value) =>
    REUPLOAD_OPTIONS.find((option) => option.value === value)?.label || value;

  const buildRejectionPayload = () => {
    const selectedReasons = rejectionForm.reasons.map(getRejectionReasonLabel);
    const customReason = rejectionForm.customReason.trim();
    const reasons = customReason
      ? [...selectedReasons, customReason]
      : selectedReasons;

    const reuploadItems = rejectionForm.requestReupload
      ? rejectionForm.reuploadItems.map(getReuploadLabel)
      : [];

    const missingDetails = rejectionForm.missingDetails
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const sections = [];

    if (reasons.length > 0) {
      sections.push(
        `Rejection reasons:\n${reasons
          .map((reason, index) => `${index + 1}. ${reason}`)
          .join("\n")}`,
      );
    }

    if (reuploadItems.length > 0) {
      sections.push(
        `Please re-upload:\n${reuploadItems
          .map((item) => `- ${item}`)
          .join("\n")}`,
      );
    }

    if (missingDetails.length > 0) {
      sections.push(
        `Missing details to update:\n${missingDetails
          .map((item) => `- ${item}`)
          .join("\n")}`,
      );
    }

    return {
      reasons,
      reuploadItems,
      missingDetails,
      rejectionReasonText: sections.join("\n\n"),
    };
  };

  const handleRejectClient = async () => {
    const client = rejectionDialog.client;
    if (!client?.id) return;

    const payload = buildRejectionPayload();
    if (payload.reasons.length === 0) {
      showSnackbar("Please select at least one rejection reason", "warning");
      return;
    }

    try {
      setRejectionSubmitting(true);
      const rejectProviderApplication = httpsCallable(
        functions,
        "rejectProviderApplication",
      );

      const result = await rejectProviderApplication({
        userId: client.id,
        reasons: payload.reasons,
        reuploadItems: payload.reuploadItems,
        missingDetails: payload.missingDetails,
      });
      const resultData = result?.data || {};
      const emailSent = resultData.emailSent === true;
      const emailQueued = resultData.emailQueued === true;
      const emailError =
        typeof resultData.emailError === "string" ? resultData.emailError : "";

      setPendingClients((prev) => prev.filter((c) => c.id !== client.id));

      showSnackbar(
        emailSent || emailQueued
          ? `Client ${client.name || "Provider"} rejected and email ${
              emailSent ? "sent" : "queued"
            }.`
          : `Client ${client.name || "Provider"} rejected, but email queue failed${
              emailError ? `: ${emailError}` : "."
            }`,
        emailSent || emailQueued ? "success" : "warning",
      );

      setConfirmDialog({ open: false, client: null, action: null });
      setViewDialogOpen(false);
      setViewDialogData(null);
      setRejectionDialog({ open: false, client: null });
      setRejectionForm(createEmptyRejectionForm());
    } catch (err) {
      console.error("Error rejecting client:", err);
      const rawMessage =
        err?.message || "Failed to reject client and send rejection email";
      const cleanMessage = rawMessage.replace(/^Firebase:\s*/i, "").trim();
      showSnackbar(cleanMessage, "error");
    } finally {
      setRejectionSubmitting(false);
    }
  };

  // Load verification status for current client
  const loadVerificationStatus = async (clientId) => {
    try {
      const userDoc = await getDoc(doc(db, "users", clientId));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setVerificationStatus({
          documents: data.verification?.documents || {},
          banking: data.verification?.banking || {
            verified: false,
            remark: "",
          },
          visa: data.verification?.visa || { verified: false, remark: "" },
          business: data.verification?.business || {
            verified: false,
            remark: "",
          },
        });
      }
    } catch (err) {
      console.error("Error loading verification status:", err);
    }
  };

  // Handle individual verification
  const handleVerify = async (section, field = null) => {
    if (!currentClientId) return;

    try {
      const updatePath = field
        ? `verification.${section}.${field}`
        : `verification.${section}`;

      await updateDoc(doc(db, "users", currentClientId), {
        [updatePath + ".verified"]: true,
        [updatePath + ".verifiedAt"]: Timestamp.now(),
        [updatePath + ".verifiedBy"]: "admin",
      });

      // Reload verification status
      await loadVerificationStatus(currentClientId);
      showSnackbar(
        `${section} ${field || ""} verified successfully`,
        "success",
      );
    } catch (err) {
      console.error("Error verifying:", err);
      showSnackbar("Failed to verify", "error");
    }
  };

  return (
    <Box>
      {/* <Typography variant="h4" gutterBottom>
        Client Approvals
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Review and approve new service provider applications. Clients will receive access within 24 hours of approval.
      </Typography> */}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Typography>Loading pending applications...</Typography>
      ) : pendingClients.length === 0 ? (
        <Alert severity="info">
          <Typography variant="body1">
            Great! No pending client applications at the moment.
          </Typography>
        </Alert>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>
                {pendingClients.length} client application
                {pendingClients.length !== 1 ? "s" : ""} pending review
              </strong>
              <br />
              Applications are automatically processed within 24 hours of
              submission.
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            {pendingClients.map((client) => (
              <Grid item size={{ xs: 12, md: 6, lg: 4 }} key={client.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Header */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Person sx={{ mr: 1, color: "primary.main" }} />
                        <Typography variant="h6" component="div">
                          {client.name || "Name Not Provided"}
                        </Typography>
                      </Box>

                      <Chip
                        icon={<HourglassEmpty />}
                        label="Pending Review"
                        color="warning"
                        size="small"
                        sx={{ mb: 2 }}
                      />
                    </Box>

                    {/* Personal Info */}
                    <Box sx={{ mb: 2 }}>
                      {/* <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Person sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                          {client.name || 'Name Not Provided'}
                        </Typography>
                      </Box> */}

                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Email
                          sx={{ fontSize: 16, mr: 1, color: "text.secondary" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {client.email || "Email Not Provided"}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Phone
                          sx={{ fontSize: 16, mr: 1, color: "text.secondary" }}
                        />
                        <Typography variant="body2">
                          {client.phone || "Phone Not Provided"}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Business Details */}
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      color="text.secondary"
                    >
                      Business Information
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Type:</strong>{" "}
                      {client.businessType || "Not specified"}
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Experience:</strong>{" "}
                      {client.experience || "Not specified"}
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Location:</strong>{" "}
                      {typeof client.address === "string"
                        ? client.address
                        : client.address?.city || "Not specified"}
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>Applied:</strong> {formatDate(client.createdAt)}
                    </Typography>

                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircle />}
                        onClick={() => handleApprovalAction(client, "approved")}
                        fullWidth
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<Cancel />}
                        onClick={() => handleApprovalAction(client, "rejected")}
                        fullWidth
                      >
                        Reject
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={async () => {
                          setViewLoading(true);
                          setViewDialogData(null);
                          setViewDialogOpen(true);
                          setCurrentClientId(client.id);

                          try {
                            const userId = client.id;

                            // Load verification status
                            await loadVerificationStatus(userId);

                            let top = null;
                            try {
                              const userDoc = await getDoc(
                                doc(db, "users", userId),
                              );
                              if (userDoc.exists()) top = userDoc.data();
                            } catch (err) {
                              console.warn(
                                "Failed to fetch top-level user doc",
                                err,
                              );
                            }

                            // fetch details subcollection if any
                            const detailsRef = collection(
                              db,
                              "users",
                              userId,
                              "details",
                            );
                            const detailsSnap = await getDocs(detailsRef);
                            const detailsMap = {};
                            for (const d of detailsSnap.docs)
                              detailsMap[d.id] = d.data();

                            // check for provider_onboarding in details or at root subcollection
                            const onboardingRef = collection(
                              db,
                              "users",
                              userId,
                              "provider_onboarding",
                            );
                            const onboardSnap = await getDocs(onboardingRef);
                            const onboard = {};
                            for (const d of onboardSnap.docs)
                              onboard[d.id] = d.data();

                            // if not found at root, also check details/provider_onboarding
                            if (
                              Object.keys(onboard).length === 0 &&
                              detailsMap["provider_onboarding"]
                            ) {
                              Object.assign(onboard, {
                                provider_onboarding:
                                  detailsMap["provider_onboarding"],
                              });
                            }

                            // Merge data from details subcollection like in Clients.js
                            const merged = { ...top };

                            // Helper to find a details doc by multiple possible id variants
                            const findDetailsDoc = (candidates) => {
                              for (const name of candidates) {
                                if (detailsMap[name]) return detailsMap[name];
                              }
                              return null;
                            };

                            // Find common detail docs
                            const bankingDoc = findDetailsDoc([
                              "bankingDetails",
                              "banking",
                              "bank_details",
                              "banking_details",
                            ]);
                            const businessDoc = findDetailsDoc([
                              "businessInformation",
                              "business_info",
                              "business_information",
                            ]);
                            const profileDoc = findDetailsDoc([
                              "profile",
                              "profileInfo",
                              "profile_info",
                            ]);
                            const onboardingDoc =
                              findDetailsDoc([
                                "provider_onboarding",
                                "providerOnboarding",
                                "onboarding",
                              ]) || Object.values(onboard)[0];

                            if (bankingDoc) merged.bankingDetails = bankingDoc;
                            if (businessDoc)
                              merged.businessInformation = businessDoc;
                            if (profileDoc)
                              merged.profile = {
                                ...(merged.profile || {}),
                                ...profileDoc,
                              };
                            if (onboardingDoc)
                              merged.provider_onboarding = onboardingDoc;

                            // If provider_onboarding contains nested sections, copy them to merged
                            if (merged.provider_onboarding) {
                              if (merged.provider_onboarding.bankingDetails) {
                                merged.bankingDetails =
                                  merged.bankingDetails ||
                                  merged.provider_onboarding.bankingDetails;
                              }
                              if (
                                merged.provider_onboarding.businessInformation
                              ) {
                                merged.businessInformation =
                                  merged.businessInformation ||
                                  merged.provider_onboarding
                                    .businessInformation;
                              }
                              if (merged.provider_onboarding.profile) {
                                merged.profile = {
                                  ...(merged.profile || {}),
                                  ...merged.provider_onboarding.profile,
                                };
                              }
                              if (merged.provider_onboarding.documents) {
                                merged.documents =
                                  merged.documents ||
                                  merged.provider_onboarding.documents;
                              }
                            }

                            // Copy banking fields to root
                            if (merged.bankingDetails) {
                              merged.bankName =
                                merged.bankName ||
                                merged.bankingDetails.bankName ||
                                merged.bankingDetails.bank_name ||
                                merged.bankingDetails.bank;
                              merged.accountName =
                                merged.accountName ||
                                merged.bankingDetails.accountName ||
                                merged.bankingDetails.account_name ||
                                merged.bankingDetails.account;
                              merged.bsb =
                                merged.bsb ||
                                merged.bankingDetails.bsb ||
                                merged.bankingDetails.bsbNumber;
                              merged.accountNumber =
                                merged.accountNumber ||
                                merged.bankingDetails.accountNumber ||
                                merged.bankingDetails.account_number;
                            }

                            // Copy business fields to root
                            if (merged.businessInformation) {
                              merged.abn =
                                merged.abn ||
                                merged.businessInformation.abnNumber ||
                                merged.businessInformation.abn_number ||
                                merged.businessInformation.abn;
                              merged.tfn =
                                merged.tfn ||
                                merged.businessInformation.tfnNumber ||
                                merged.businessInformation.tfn_number ||
                                merged.businessInformation.tfn;
                              merged.businessName =
                                merged.businessName ||
                                merged.businessInformation.businessName ||
                                merged.businessInformation.business_name;
                            }

                            // Copy visa fields from provider_onboarding
                            if (merged.provider_onboarding) {
                              merged.visaCategory =
                                merged.visaCategory ||
                                merged.provider_onboarding.visaCategory ||
                                merged.provider_onboarding.visa_category ||
                                merged.provider_onboarding.visaType;
                              merged.visaExpiry =
                                merged.visaExpiry ||
                                merged.provider_onboarding.visaExpiry ||
                                merged.provider_onboarding.visa_expiry;
                              merged.nationalityStatus =
                                merged.nationalityStatus ||
                                merged.provider_onboarding.nationalityStatus ||
                                merged.provider_onboarding.nationality_status;
                            }

                            // Copy from profile
                            if (merged.profile) {
                              merged.nationalityStatus =
                                merged.nationalityStatus ||
                                merged.profile.nationalityStatus ||
                                merged.profile.nationality_status;
                              merged.visaCategory =
                                merged.visaCategory ||
                                merged.profile.visaCategory ||
                                merged.profile.visa_category;
                              merged.visaExpiry =
                                merged.visaExpiry ||
                                merged.profile.visaExpiry ||
                                merged.profile.visa_expiry;
                            }

                            setViewDialogData({
                              topLevel: merged,
                              details: detailsMap,
                              provider_onboarding: onboard,
                            });
                          } catch (err) {
                            console.error(
                              "Failed to load request details",
                              err,
                            );
                            setViewDialogData({ error: String(err) });
                          } finally {
                            setViewLoading(false);
                          }
                        }}
                        fullWidth
                      >
                        View
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({ open: false, client: null, action: null })
        }
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {confirmDialog.action === "approved"
            ? "Approve Client Application"
            : "Reject Client Application"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {confirmDialog.action === "approved" ? (
              <>
                Are you sure you want to <strong>approve</strong>{" "}
                {confirmDialog.client?.name}'s application for{" "}
                <strong>{confirmDialog.client?.businessName}</strong>?
                <br />
                <br />
                Once approved, they will be able to login and access all client
                features immediately.
              </>
            ) : (
              <>
                Are you sure you want to <strong>reject</strong>{" "}
                {confirmDialog.client?.name}'s application for{" "}
                <strong>{confirmDialog.client?.businessName}</strong>?
                <br />
                <br />
                This action cannot be undone. The client will not be able to
                access the platform.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({ open: false, client: null, action: null })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={confirmApprovalAction}
            autoFocus
            color={confirmDialog.action === "approved" ? "success" : "error"}
            variant="contained"
          >
            {confirmDialog.action === "approved" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View full request details dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setViewDialogData(null);
        }}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            maxWidth: 1500,
            width: "calc(100% - 32px)",
            overflowX: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              startIcon={<ArrowBackIosNew />}
              onClick={() => {
                setViewDialogOpen(false);
                setViewDialogData(null);
              }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowX: "hidden" }}>
          {viewLoading ? (
            <div>Loading...</div>
          ) : viewDialogData ? (
            <Box>
              {(() => {
                const top = viewDialogData.topLevel || {};
                const po = viewDialogData.provider_onboarding || {};
                const approvalStatus =
                  top.approvalStatus ||
                  top.approval_status ||
                  (Object.values(po)[0] &&
                    (Object.values(po)[0].approvalStatus ||
                      Object.values(po)[0].approval_status)) ||
                  null;
                return (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          cursor: top?.photoURL ? "pointer" : "default",
                          "&:hover": top?.photoURL
                            ? {
                                opacity: 0.8,
                                transform: "scale(1.05)",
                                transition: "all 0.2s",
                              }
                            : {},
                        }}
                        src={top?.photoURL}
                        onClick={() => {
                          if (top?.photoURL) {
                            handleImageClick(
                              top.photoURL,
                              `${top?.name || "Provider"} - Profile Photo`,
                            );
                          }
                        }}
                      >
                        <Person />
                      </Avatar>
                      <Box>
                        <Typography variant="h6">
                          {top?.name || "Unknown"}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            mt: 1,
                            alignItems: "center",
                          }}
                        >
                          {top?.onboardingDocumentsVerified && (
                            <Chip
                              label="Verified"
                              size="small"
                              color="success"
                            />
                          )}
                          {top?.inactive ? (
                            <Chip label="Inactive" size="small" color="error" />
                          ) : (
                            <Chip label="Active" size="small" color="primary" />
                          )}
                          {top?.activeRole && (
                            <Chip label={top.activeRole} size="small" />
                          )}
                          {approvalStatus && (
                            <Chip
                              label={
                                typeof approvalStatus === "string"
                                  ? approvalStatus.charAt(0).toUpperCase() +
                                    approvalStatus.slice(1)
                                  : String(approvalStatus)
                              }
                              size="small"
                              color={
                                ("" + approvalStatus).toLowerCase() ===
                                "approved"
                                  ? "success"
                                  : ("" + approvalStatus).toLowerCase() ===
                                      "pending"
                                    ? "warning"
                                    : ("" + approvalStatus).toLowerCase() ===
                                        "rejected"
                                      ? "error"
                                      : "default"
                              }
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>

                    <ProviderDocumentReviewList providerId={currentClientId} />

                    <Grid container spacing={2} alignItems="stretch">
                      <Grid item size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: "flex", minWidth: 0 }}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              alignItems: "center",
                            }}
                          >
                            <Person sx={{ fontSize: 20 }} />
                            <Typography variant="h6">
                              Personal Information
                            </Typography>
                          </Box>
                          <Box
                            sx={{ mt: 2, display: "grid", gap: 1.5, flex: 1 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "center",
                              }}
                            >
                              <Email sx={{ color: "text.secondary" }} />
                              <Box>
                                <Typography variant="body2">Email</Typography>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {top?.email || (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      Not added yet
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "center",
                              }}
                            >
                              <Phone sx={{ color: "text.secondary" }} />
                              <Box>
                                <Typography variant="body2">Phone</Typography>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {top?.phone || (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      Not added yet
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "center",
                              }}
                            >
                              <LocationOn sx={{ color: "text.secondary" }} />
                              <Box>
                                <Typography variant="body2">Address</Typography>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {top?.address || (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      Not added yet
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: "flex", minWidth: 0 }}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: "center",
                              }}
                            >
                              <HourglassEmpty sx={{ fontSize: 20 }} />
                              <Typography variant="h6">
                                Visa Information
                              </Typography>
                            </Box>
                            {verificationStatus.visa?.verified ? (
                              <Chip
                                icon={<Verified />}
                                label="Verified"
                                size="small"
                                color="success"
                              />
                            ) : (
                              <Chip
                                icon={<Warning />}
                                label="Unverified"
                                size="small"
                                color="warning"
                              />
                            )}
                          </Box>
                          <Box
                            sx={{ mt: 2, display: "grid", gap: 1.5, flex: 1 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "center",
                              }}
                            >
                              <CalendarToday sx={{ color: "text.secondary" }} />
                              <Box>
                                <Typography variant="body2">
                                  Nationality Status
                                </Typography>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {top?.nationalityStatus || (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      Not added yet
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "center",
                              }}
                            >
                              <Language sx={{ color: "text.secondary" }} />
                              <Box>
                                <Typography variant="body2">
                                  Visa Category
                                </Typography>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {top?.visaCategory || (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      Not added yet
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "center",
                              }}
                            >
                              <CalendarToday sx={{ color: "text.secondary" }} />
                              <Box>
                                <Typography variant="body2">
                                  Visa Expiry
                                </Typography>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {top?.visaExpiry
                                    ? formatDate(top.visaExpiry)
                                    : (
                                      <Typography
                                        variant="body2"
                                        color="text.disabled"
                                        sx={{ fontStyle: "italic" }}
                                      >
                                        Not added yet
                                      </Typography>
                                    )}
                                </Typography>
                              </Box>
                            </Box>
                            {verificationStatus.visa?.remark && (
                              <Alert
                                severity="info"
                                icon={<Comment />}
                                sx={{ mt: 1 }}
                              >
                                <Typography variant="caption">
                                  <strong>Remark:</strong>{" "}
                                  {verificationStatus.visa.remark}
                                </Typography>
                              </Alert>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<Verified />}
                              onClick={() => handleVerify("visa")}
                              disabled={verificationStatus.visa?.verified}
                              fullWidth
                            >
                              Verify
                            </Button>
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: "flex", minWidth: 0 }}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: "center",
                              }}
                            >
                              <AccountBalance sx={{ fontSize: 20 }} />
                              <Typography variant="h6">
                                Banking Details
                              </Typography>
                            </Box>
                            {verificationStatus.banking?.verified ? (
                              <Chip
                                icon={<Verified />}
                                label="Verified"
                                size="small"
                                color="success"
                              />
                            ) : (
                              <Chip
                                icon={<Warning />}
                                label="Unverified"
                                size="small"
                                color="warning"
                              />
                            )}
                          </Box>
                          <Box
                            sx={{ mt: 2, display: "grid", gap: 1.5, flex: 1 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "center",
                              }}
                            >
                              <AccountBalance
                                sx={{ color: "text.secondary" }}
                              />
                              <Box>
                                <Typography variant="body2">
                                  Bank Name
                                </Typography>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {top?.bankName || (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      Not added yet
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "center",
                              }}
                            >
                              <AccountBalance
                                sx={{ color: "text.secondary" }}
                              />
                              <Box>
                                <Typography variant="body2">
                                  Account Name
                                </Typography>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {top?.accountName || (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      Not added yet
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "center",
                              }}
                            >
                              <CreditCard sx={{ color: "text.secondary" }} />
                              <Box>
                                <Typography variant="body2">BSB</Typography>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {top?.bsb || (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      Not added yet
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1.5,
                                alignItems: "center",
                              }}
                            >
                              <CreditCard sx={{ color: "text.secondary" }} />
                              <Box>
                                <Typography variant="body2">
                                  Account Number
                                </Typography>
                                <Typography sx={{ fontWeight: 600 }}>
                                  {top?.accountNumber || (
                                    <Typography
                                      variant="body2"
                                      color="text.disabled"
                                      sx={{ fontStyle: "italic" }}
                                    >
                                      Not added yet
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                            {verificationStatus.banking?.remark && (
                              <Alert
                                severity="info"
                                icon={<Comment />}
                                sx={{ mt: 1 }}
                              >
                                <Typography variant="caption">
                                  <strong>Remark:</strong>{" "}
                                  {verificationStatus.banking.remark}
                                </Typography>
                              </Alert>
                            )}
                          </Box>
                          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<Verified />}
                              onClick={() => handleVerify("banking")}
                              disabled={verificationStatus.banking?.verified}
                              fullWidth
                            >
                              Verify
                            </Button>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      spacing={2}
                      alignItems="stretch"
                      sx={{ mt: 3 }}
                    >
                      <Grid item size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: "flex", minWidth: 0 }}>
                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                          variant="outlined"
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="h6">
                              Business Information
                            </Typography>
                            {verificationStatus.business?.verified ? (
                              <Chip
                                icon={<Verified />}
                                label="Verified"
                                size="small"
                                color="success"
                              />
                            ) : (
                              <Chip
                                icon={<Warning />}
                                label="Unverified"
                                size="small"
                                color="warning"
                              />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Business Name
                          </Typography>
                          <Typography sx={{ fontWeight: 600 }}>
                            {top?.businessName || (
                              <Typography
                                variant="body2"
                                color="text.disabled"
                                sx={{ fontStyle: "italic" }}
                              >
                                Not added yet
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            ABN
                          </Typography>
                          <Typography sx={{ fontWeight: 600 }}>
                            {top?.abn || (
                              <Typography
                                variant="body2"
                                color="text.disabled"
                                sx={{ fontStyle: "italic" }}
                              >
                                Not added yet
                              </Typography>
                            )}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Link
                              href={
                                top?.abn
                                  ? `https://abr.business.gov.au/ABN/View?id=${top?.abn}`
                                  : "#"
                              }
                              target="_blank"
                              rel="noreferrer"
                            >
                              Verify on ABR website
                            </Link>
                          </Box>
                          {verificationStatus.business?.remark && (
                            <Alert
                              severity="info"
                              icon={<Comment />}
                              sx={{ mt: 2 }}
                            >
                              <Typography variant="caption">
                                <strong>Remark:</strong>{" "}
                                {verificationStatus.business.remark}
                              </Typography>
                            </Alert>
                          )}
                          <Box
                            sx={{ display: "flex", gap: 1, mt: "auto", pt: 2 }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<Verified />}
                              onClick={() => handleVerify("business")}
                              disabled={verificationStatus.business?.verified}
                              fullWidth
                            >
                              Verify
                            </Button>
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item size={{ xs: 12, md: 6, lg: 4 }} sx={{ display: "flex", minWidth: 0 }}>
                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                          variant="outlined"
                        >
                          <Typography variant="h6">
                            Notification Preferences
                          </Typography>
                          <Box sx={{ mt: 2 }}>
                            <FormControl component="fieldset">
                              <RadioGroup
                                value={
                                  top?.notificationPreference || "email_sms"
                                }
                              >
                                <FormControlLabel
                                  value="email"
                                  control={<Radio />}
                                  label={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        alignItems: "center",
                                      }}
                                    >
                                      <Email sx={{ color: "text.secondary" }} />
                                      <Typography>Email Only</Typography>
                                    </Box>
                                  }
                                />
                                <FormControlLabel
                                  value="sms"
                                  control={<Radio />}
                                  label={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        alignItems: "center",
                                      }}
                                    >
                                      <Phone sx={{ color: "text.secondary" }} />
                                      <Typography>SMS Only</Typography>
                                    </Box>
                                  }
                                />
                                <FormControlLabel
                                  value="email_sms"
                                  control={<Radio />}
                                  label={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        alignItems: "center",
                                      }}
                                    >
                                      <NotificationsActive
                                        sx={{ color: "text.secondary" }}
                                      />
                                      <Typography>Email & SMS Both</Typography>
                                    </Box>
                                  }
                                />
                                <FormControlLabel
                                  value="none"
                                  control={<Radio />}
                                  label={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        alignItems: "center",
                                      }}
                                    >
                                      <NotificationsActive
                                        sx={{ color: "text.secondary" }}
                                      />
                                      <Typography>No Notifications</Typography>
                                    </Box>
                                  }
                                />
                              </RadioGroup>
                            </FormControl>
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item size={{ xs: 12 }} sx={{ display: "flex", minWidth: 0 }}>
                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                          variant="outlined"
                        >
                          <Typography variant="h6">Documents</Typography>
                          {/* <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            Uploaded documents (click to open)
                          </Typography> */}
                          <Grid container spacing={2} sx={{ mt: 2, flex: 1 }}>
                            {(function () {
                              const findFirstUrl = (source, keys) => {
                                for (const key of keys) {
                                  const value = source?.[key];
                                  if (!value) continue;
                                  if (
                                    typeof value === "string" &&
                                    value.trim().length > 0
                                  ) {
                                    return value;
                                  }
                                  if (Array.isArray(value)) {
                                    const firstString = value.find(
                                      (item) =>
                                        typeof item === "string" &&
                                        item.trim().length > 0,
                                    );
                                    if (firstString) return firstString;
                                  }
                                  if (typeof value === "object") {
                                    const objectUrl =
                                      (typeof value.url === "string" &&
                                        value.url.trim().length > 0 &&
                                        value.url) ||
                                      (typeof value.downloadURL === "string" &&
                                        value.downloadURL.trim().length > 0 &&
                                        value.downloadURL) ||
                                      (typeof value.fileUrl === "string" &&
                                        value.fileUrl.trim().length > 0 &&
                                        value.fileUrl) ||
                                      (typeof value.uri === "string" &&
                                        value.uri.trim().length > 0 &&
                                        value.uri);
                                    if (objectUrl) return objectUrl;
                                  }
                                }
                                return null;
                              };

                              const onboardingDoc =
                                top?.provider_onboarding ||
                                Object.values(po).find(
                                  (entry) =>
                                    entry &&
                                    typeof entry === "object" &&
                                    entry.documents,
                                ) ||
                                Object.values(po).find(
                                  (entry) =>
                                    entry && typeof entry === "object",
                                ) ||
                                {};
                              const docsMap = {
                                ...(top?.documents || {}),
                                ...(onboardingDoc?.documents || {}),
                              };
                              const meta = [
                                {
                                  label: "Passport",
                                  keys: [
                                    "passportUrl",
                                    "passport",
                                    "idProofUrl",
                                    "idProof",
                                  ],
                                  Icon: VerifiedUser,
                                  color: "#2563eb",
                                  verifyField: "passport",
                                  verificationFields: ["passport", "idProof"],
                                },
                                {
                                  label: "Driving Licence",
                                  keys: [
                                    "drivingLicenceUrl",
                                    "drivingLicenseUrl",
                                    "drivingLicence",
                                    "drivingLicense",
                                  ],
                                  Icon: CreditCard,
                                  color: "#7c3aed",
                                  verifyField: "drivingLicence",
                                  verificationFields: [
                                    "drivingLicence",
                                    "drivingLicense",
                                    "idProof",
                                  ],
                                },
                                {
                                  label: "Resume/CV",
                                  keys: ["resumeUrl", "resume", "cvUrl", "cv"],
                                  Icon: Article,
                                  color: "#059669",
                                  verifyField: "resume",
                                  verificationFields: ["resume"],
                                },
                                {
                                  label: "Certificates",
                                  keys: [
                                    "certificatesUrl",
                                    "certificates",
                                    "certificateUrl",
                                    "certificate",
                                  ],
                                  Icon: School,
                                  color: "#f59e0b",
                                  verifyField: "certificates",
                                  verificationFields: ["certificates"],
                                },
                                {
                                  label: "Verification Video",
                                  keys: [
                                    "verificationVideo",
                                    "verificationVideoUrl",
                                  ],
                                  Icon: Videocam,
                                  color: "#ef4444",
                                  verifyField: "verificationVideo",
                                  verificationFields: ["verificationVideo"],
                                },
                              ];
                              return meta.map(
                                ({
                                  label,
                                  keys,
                                  Icon,
                                  color,
                                  verifyField,
                                  verificationFields,
                                }) => {
                                  const url = findFirstUrl(docsMap, keys);
                                  const verificationEntries =
                                    verificationFields
                                      .map(
                                        (name) =>
                                          verificationStatus.documents?.[name],
                                      )
                                      .filter(Boolean);
                                  const isVerified = verificationEntries.some(
                                    (entry) => entry?.verified,
                                  );
                                  const remark = verificationEntries
                                    .map((entry) => entry?.remark)
                                    .find(Boolean);
                                  const normalizedUrl = (
                                    url || ""
                                  ).toLowerCase();
                                  const expiryValue =
                                    verifyField === "passport" ?
                                      resolveDocumentExpiryForRequest(
                                        top,
                                        onboardingDoc,
                                        "passport",
                                      ) :
                                    verifyField === "drivingLicence" ?
                                      resolveDocumentExpiryForRequest(
                                        top,
                                        onboardingDoc,
                                        "drivingLicence",
                                      ) :
                                      null;
                                  return (
                                    <Grid item size={{ xs: 12 }} key={label}>
                                      <Paper
                                        variant="outlined"
                                        sx={{ p: 2, borderRadius: 2 }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            justifyContent: "space-between",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                bgcolor: "#f3f4f6",
                                                borderRadius: 1.5,
                                                p: 1,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                              }}
                                            >
                                              <Icon
                                                sx={{ color, fontSize: 20 }}
                                              />
                                            </Box>
                                            <Typography
                                              sx={{ fontWeight: 600 }}
                                            >
                                              {label}
                                            </Typography>
                                          </Box>
                                          {isVerified && (
                                            <Chip
                                              icon={<Verified />}
                                              label="Verified"
                                              size="small"
                                              color="success"
                                            />
                                          )}
                                        </Box>
                                        <Box sx={{ mt: 1 }}>
                                          {url ? (
                                            <Box>
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "center",
                                                  mb: 1,
                                                }}
                                              >
                                                <Typography
                                                  variant="body2"
                                                  color="text.secondary"
                                                >
                                                  Uploaded
                                                </Typography>
                                                {verifyField ===
                                                  "verificationVideo" ||
                                                normalizedUrl.includes(
                                                  ".mp4",
                                                ) ||
                                                normalizedUrl.includes(
                                                  ".mov",
                                                ) ||
                                                normalizedUrl.includes(
                                                  ".avi",
                                                ) ? (
                                                  <Button
                                                    size="small"
                                                    href={url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                  >
                                                    Open Video
                                                  </Button>
                                                ) : (
                                                  <Button
                                                    size="small"
                                                    onClick={() =>
                                                      handleImageClick(
                                                        url,
                                                        label,
                                                      )
                                                    }
                                                  >
                                                    View
                                                  </Button>
                                                )}
                                              </Box>
                                              {(verifyField ===
                                                "passport" ||
                                                verifyField ===
                                                  "drivingLicence") && (
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                      "space-between",
                                                    alignItems: "center",
                                                    mb: 1,
                                                  }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                  >
                                                    Expiry Date
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 600 }}
                                                  >
                                                    {expiryValue ?
                                                      formatDate(
                                                        expiryValue,
                                                      ) :
                                                      "Not added yet"}
                                                  </Typography>
                                                </Box>
                                              )}
                                              {remark && (
                                                <Alert
                                                  severity="info"
                                                  icon={<Comment />}
                                                  sx={{ mb: 1 }}
                                                >
                                                  <Typography variant="caption">
                                                    <strong>Remark:</strong>{" "}
                                                    {remark}
                                                  </Typography>
                                                </Alert>
                                              )}
                                              <Box
                                                sx={{ display: "flex", gap: 1 }}
                                              >
                                                <Button
                                                  size="small"
                                                  variant="contained"
                                                  color="success"
                                                  startIcon={<Verified />}
                                                  onClick={() =>
                                                    handleVerify(
                                                      "documents",
                                                      verifyField,
                                                    )
                                                  }
                                                  disabled={isVerified}
                                                  fullWidth
                                                >
                                                  Verify
                                                </Button>
                                              </Box>
                                            </Box>
                                          ) : (
                                            <Box>
                                              <Typography
                                                variant="body2"
                                                color="text.disabled"
                                                sx={{
                                                  mb: 1,
                                                  fontStyle: "italic",
                                                }}
                                              >
                                                Not uploaded yet
                                              </Typography>
                                              {(verifyField ===
                                                "passport" ||
                                                verifyField ===
                                                  "drivingLicence") && (
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                      "space-between",
                                                    alignItems: "center",
                                                    mb: 1,
                                                  }}
                                                >
                                                  <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                  >
                                                    Expiry Date
                                                  </Typography>
                                                  <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 600 }}
                                                  >
                                                    {expiryValue ?
                                                      formatDate(
                                                        expiryValue,
                                                      ) :
                                                      "Not added yet"}
                                                  </Typography>
                                                </Box>
                                              )}
                                              {remark && (
                                                <Alert
                                                  severity="info"
                                                  icon={<Comment />}
                                                  sx={{ mb: 1 }}
                                                >
                                                  <Typography variant="caption">
                                                    <strong>Remark:</strong>{" "}
                                                    {remark}
                                                  </Typography>
                                                </Alert>
                                              )}
                                            </Box>
                                          )}
                                        </Box>
                                      </Paper>
                                    </Grid>
                                  );
                                },
                              );
                            })()}
                          </Grid>
                        </Paper>
                      </Grid>
                    </Grid>
                  </>
                );
              })()}

              {/* <Box sx={{ mt: 2 }}>
                <Button size="small" onClick={() => setViewShowRaw(prev => !prev)} sx={{ mb: 1 }}>{viewShowRaw ? 'Hide raw data' : 'Show raw data'}</Button>
                {viewShowRaw ? (
                  <Box>
                    <Typography variant="subtitle2">Raw data (for debugging)</Typography>
                    <pre style={{ maxHeight: 220, overflow: 'auto' }}>{JSON.stringify({ topLevel: viewDialogData.topLevel, details: viewDialogData.details, provider_onboarding: viewDialogData.provider_onboarding }, null, 2)}</pre>
                  </Box>
                ) : null}
              </Box> */}
            </Box>
          ) : (
            <Typography>No data available</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => {
                  if (currentClientId) {
                    const client =
                      pendingClients.find((c) => c.id === currentClientId) ||
                      (viewDialogData?.topLevel
                        ? { id: currentClientId, ...viewDialogData.topLevel }
                        : null);
                    handleApprovalAction(client, "approved");
                    setViewDialogOpen(false);
                    setViewDialogData(null);
                  }
                }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={() => {
                  if (currentClientId) {
                    const client =
                      pendingClients.find((c) => c.id === currentClientId) ||
                      (viewDialogData?.topLevel
                        ? { id: currentClientId, ...viewDialogData.topLevel }
                        : null);
                    handleApprovalAction(client, "rejected");
                  }
                }}
              >
                Reject
              </Button>
            </Box>
            <Button
              onClick={() => {
                setViewDialogOpen(false);
                setViewDialogData(null);
              }}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog
        open={rejectionDialog.open}
        onClose={closeRejectionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Provider Application</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select one or more rejection reasons. You can also request
            re-upload of documents or ask for missing details. The provider
            will receive this by email.
          </DialogContentText>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Rejection reasons (multiple allowed)
          </Typography>
          <FormGroup sx={{ mb: 2 }}>
            {REJECTION_REASON_OPTIONS.map((reason) => (
              <FormControlLabel
                key={reason.value}
                control={
                  <Checkbox
                    checked={rejectionForm.reasons.includes(reason.value)}
                    onChange={() => toggleRejectionReason(reason.value)}
                  />
                }
                label={reason.label}
              />
            ))}
          </FormGroup>

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Additional rejection reason (optional)"
            placeholder="Add any additional reason not listed above"
            value={rejectionForm.customReason}
            onChange={(e) =>
              setRejectionForm((prev) => ({
                ...prev,
                customReason: e.target.value,
              }))
            }
            sx={{ mb: 2 }}
          />

          <Divider sx={{ mb: 2 }} />

          <FormControlLabel
            control={
              <Checkbox
                checked={rejectionForm.requestReupload}
                onChange={(e) =>
                  setRejectionForm((prev) => ({
                    ...prev,
                    requestReupload: e.target.checked,
                    reuploadItems: e.target.checked ? prev.reuploadItems : [],
                  }))
                }
              />
            }
            label="Request re-upload of documents"
            sx={{ mb: 1 }}
          />

          {rejectionForm.requestReupload && (
            <FormGroup sx={{ mb: 2, pl: 1 }}>
              {REUPLOAD_OPTIONS.map((item) => (
                <FormControlLabel
                  key={item.value}
                  control={
                    <Checkbox
                      checked={rejectionForm.reuploadItems.includes(item.value)}
                      onChange={() => toggleReuploadItem(item.value)}
                    />
                  }
                  label={item.label}
                />
              ))}
            </FormGroup>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Missing details to update (optional)"
            placeholder={
              "One detail per line. Example:\nProvide valid visa expiry date\nUpdate account holder name"
            }
            value={rejectionForm.missingDetails}
            onChange={(e) =>
              setRejectionForm((prev) => ({
                ...prev,
                missingDetails: e.target.value,
              }))
            }
            helperText="These notes will be included in the provider email."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRejectionDialog} disabled={rejectionSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectClient}
            variant="contained"
            color="error"
            disabled={
              rejectionSubmitting ||
              (rejectionForm.reasons.length === 0 &&
                !rejectionForm.customReason.trim())
            }
            startIcon={
              rejectionSubmitting ? <CircularProgress size={16} /> : <Cancel />
            }
          >
            {rejectionSubmitting ? "Rejecting..." : "Reject & Send Email"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreview.open}
        onClose={handleCloseImagePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">{imagePreview.title}</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              href={imagePreview.url}
              target="_blank"
              rel="noreferrer"
            >
              Open in New Tab
            </Button>
            <Button size="small" onClick={handleCloseImagePreview}>
              Close
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
            }}
          >
            <img
              src={imagePreview.url}
              alt={imagePreview.title}
              style={{
                maxWidth: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientApprovals;
